import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaEdit,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaStickyNote,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import PortalLocationSelects from "./PortalLocationSelects";
import PortalSourceSelect from "./PortalSourceSelect";
import { canDeleteInPortal } from "./portalSession";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";

const CUSTOMER_STATUSES = ["Active", "Inactive"];

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayValue(value)}</span>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children, className = "" }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <h2 className="cportal-detail-section-title">
        {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
        {title}
      </h2>
      {children}
    </section>
  );
}

function initials(row) {
  const first = (row?.First_Name || "").trim().charAt(0);
  const last = (row?.Last_Name || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "C";
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function fullName(row) {
  return `${(row?.First_Name || "").trim()} ${(row?.Last_Name || "").trim()}`.trim() || "—";
}

function phoneDisplay(row) {
  const cc = row?.Country_Code ? String(row.Country_Code).trim() : "";
  const phone = row?.Phone ? String(row.Phone).trim() : "";
  if (!phone) return "—";
  return cc ? `${cc} ${phone}` : phone;
}

function toForm(row) {
  return {
    firstName: row?.First_Name || "",
    lastName: row?.Last_Name || "",
    email: row?.Email || "",
    countryCode: row?.Country_Code || "+91",
    phone: row?.Phone || "",
    altPhone: row?.Alt_Phone || "",
    addressLine1: row?.Address_Line_1 || "",
    addressLine2: row?.Address_Line_2 || "",
    countryId: row?.Country_Id ? String(row.Country_Id) : "",
    stateId: row?.State_Id ? String(row.State_Id) : "",
    cityId: row?.City_Id ? String(row.City_Id) : "",
    zipCode: row?.Zip_Code || "",
    source: row?.Source || "",
    comLang: row?.Com_Lang || "",
    status: row?.Status || "Active",
    notes: row?.Notes || "",
  };
}

function buildPayload(form) {
  const payload = {
    First_Name: form.firstName.trim(),
    Last_Name: form.lastName.trim() || null,
    Email: form.email.trim() || null,
    Country_Code: form.countryCode.trim() || null,
    Phone: form.phone.trim() || null,
    Alt_Phone: form.altPhone.trim() || null,
    Address_Line_1: form.addressLine1.trim() || null,
    Address_Line_2: form.addressLine2.trim() || null,
    Country_Id: form.countryId ? Number(form.countryId) : null,
    State_Id: form.stateId ? Number(form.stateId) : null,
    City_Id: form.cityId ? Number(form.cityId) : null,
    Zip_Code: form.zipCode.trim() || null,
    Source: form.source.trim() || null,
    Com_Lang: form.comLang.trim() || null,
    Status: form.status || "Active",
    Notes: form.notes.trim() || null,
  };
  return payload;
}

function CustomerTenantCustomerView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get(`/client/tenant-customers/${id}`);
        if (!active) return;
        const data = res.data?.data || null;
        setCustomer(data);
        setForm(toForm(data));
        if (!data) setError("Customer not found.");
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load customer details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (location.state?.edit) {
      setEditing(true);
    }
  }, [location.state?.edit]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim()) {
      setMessage({ type: "error", text: "First name is required." });
      return;
    }

    const phoneError = mobileValidationMessage(form.phone, {
      required: true,
      label: "Mobile number",
    });
    if (phoneError) {
      setMessage({ type: "error", text: phoneError });
      return;
    }
    const altPhoneError = mobileValidationMessage(form.altPhone, { label: "Alternate phone" });
    if (altPhoneError) {
      setMessage({ type: "error", text: altPhoneError });
      return;
    }
    if (!form.source.trim()) {
      setMessage({ type: "error", text: "Source is required." });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axiosInstance.put(`/client/tenant-customers/${id}`, buildPayload(form));
      const data = res.data?.data;
      if (data) {
        setCustomer(data);
        setForm(toForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Customer updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update customer.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;

    setDeleting(true);
    setMessage({ type: "", text: "" });
    try {
      await axiosInstance.delete(`/client/tenant-customers/${id}`);
      navigate("/portal/customers");
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not deactivate customer.",
      });
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => navigate("/portal/customers")}
        >
          <FaArrowLeft /> Back to customers
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Customer not found."}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => {
            setEditing(false);
            setForm(toForm(customer));
            setMessage({ type: "", text: "" });
          }}
        >
          <FaArrowLeft /> Back to customer
        </button>

        <form className="cportal-card" style={{ marginTop: 16 }} onSubmit={handleSave}>
          <div className="cportal-card-head">
            <h2>Edit customer</h2>
          </div>
          {message.text && (
            <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
              {message.text}
            </div>
          )}
          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field">
              <label>First name <span className="cportal-required">*</span></label>
              <input
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
                required
              />
            </div>
            <div className="cportal-field">
              <label>Last name</label>
              <input value={form.lastName} onChange={(e) => setField("lastName", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
            </div>
            <div className="cportal-field cportal-field-phone">
              <label>
                Mobile number <span className="cportal-required">*</span>
              </label>
              <div className="cportal-phone-row">
                <input
                  className="cportal-phone-code"
                  value={form.countryCode}
                  onChange={(e) => setField("countryCode", e.target.value)}
                />
                <input
                  value={form.phone}
                  onChange={(e) => setField("phone", sanitizeMobileInput(e.target.value))}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit number"
                  required
                />
              </div>
            </div>
            <div className="cportal-field">
              <label>Alt phone</label>
              <input
                value={form.altPhone}
                onChange={(e) => setField("altPhone", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            </div>
            <PortalSourceSelect
              value={form.source}
              onChange={(value) => setField("source", value)}
              required
            />
            <div className="cportal-field">
              <label>Communication language</label>
              <input value={form.comLang} onChange={(e) => setField("comLang", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>
                Status <span className="cportal-required">*</span>
              </label>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {CUSTOMER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <h3 className="cportal-form-section">Address</h3>
          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field">
              <label>Address line 1</label>
              <input value={form.addressLine1} onChange={(e) => setField("addressLine1", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Address line 2</label>
              <input value={form.addressLine2} onChange={(e) => setField("addressLine2", e.target.value)} />
            </div>
            <PortalLocationSelects
              countryId={form.countryId}
              stateId={form.stateId}
              cityId={form.cityId}
              onChange={({ countryId, stateId, cityId }) =>
                setForm((f) => ({ ...f, countryId, stateId, cityId }))
              }
            />
            <div className="cportal-field">
              <label>Zip code</label>
              <input value={form.zipCode} onChange={(e) => setField("zipCode", e.target.value)} />
            </div>
          </div>

          <div className="cportal-form-grid cportal-form-grid--3">
            <div className="cportal-field cportal-field--full">
              <label>Notes</label>
              <textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
            </div>
          </div>
          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={() => {
                setEditing(false);
                setForm(toForm(customer));
                setMessage({ type: "", text: "" });
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="cportal-page">
      <button
        type="button"
        className="cportal-back-link"
        onClick={() => navigate("/portal/customers")}
      >
        <FaArrowLeft /> Back to customers
      </button>

      {message.text && (
        <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="cportal-entity-hero">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar" aria-hidden="true">
            {initials(customer)}
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{fullName(customer)}</h1>
            <span className={statusBadgeClass(customer.Status)}>
              {displayValue(customer.Status)}
            </span>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-ghost"
            onClick={() => setEditing(true)}
          >
            <FaEdit aria-hidden="true" /> Edit
          </button>
          {canDelete && (
            <button
              type="button"
              className="cportal-btn cportal-btn-danger-outline"
              onClick={handleDelete}
              disabled={deleting}
            >
              <FaTrash aria-hidden="true" />
              {deleting ? "Deactivating…" : "Delete"}
            </button>
          )}
        </div>
      </div>

      <div className="cportal-detail-grid">
        <DetailSection icon={FaUser} title="Contact information">
          <div className="cportal-profile-view-grid">
            <DetailItem label="First name" value={customer.First_Name} />
            <DetailItem label="Last name" value={customer.Last_Name} />
            <DetailItem label="Email" value={customer.Email} />
            <DetailItem label="Phone">{phoneDisplay(customer)}</DetailItem>
            <DetailItem label="Alt phone" value={customer.Alt_Phone} />
          </div>
        </DetailSection>

        <DetailSection icon={FaMapMarkerAlt} title="Address">
          <div className="cportal-profile-view-grid">
            <DetailItem label="Address line 1" value={customer.Address_Line_1} />
            <DetailItem label="Address line 2" value={customer.Address_Line_2} />
            <DetailItem label="Country" value={customer.Country_Name} />
            <DetailItem label="State" value={customer.State_Name} />
            <DetailItem label="City" value={customer.City_Name} />
            <DetailItem label="Zip code" value={customer.Zip_Code} />
          </div>
        </DetailSection>
      </div>

      <DetailSection icon={FaInfoCircle} title="Other details">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Source" value={customer.Source} />
          <DetailItem label="Communication language" value={customer.Com_Lang} />
          <DetailItem label="Created on">
            <span className="cportal-datetime-value">
              <FaCalendarAlt aria-hidden="true" />
              {formatDateTime(customer.Created_On)}
            </span>
          </DetailItem>
          <DetailItem label="Updated on">
            <span className="cportal-datetime-value">
              <FaCalendarAlt aria-hidden="true" />
              {formatDateTime(customer.Updated_On)}
            </span>
          </DetailItem>
        </div>
      </DetailSection>

      <DetailSection icon={FaStickyNote} title="Notes">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Notes" value={customer.Notes} />
        </div>
      </DetailSection>
    </div>
  );
}

export default CustomerTenantCustomerView;
